/**
 * User-localized time and date utilities for Cordite CRM.
 * Default formatting uses 12-hour AM/PM with user-configured or auto-detected timezone.
 */

export type TimeFormat = "12h" | "24h";

export interface TimeZoneOption {
  value: string;
  label: string;
  region: string;
}

export const COMMON_TIMEZONES: TimeZoneOption[] = [
  // North America
  { value: "America/New_York", label: "Eastern Time (US & Canada) (ET)", region: "North America" },
  { value: "America/Chicago", label: "Central Time (US & Canada) (CT)", region: "North America" },
  { value: "America/Denver", label: "Mountain Time (US & Canada) (MT)", region: "North America" },
  { value: "America/Los_Angeles", label: "Pacific Time (US & Canada) (PT)", region: "North America" },
  { value: "America/Anchorage", label: "Alaska (AKT)", region: "North America" },
  { value: "Pacific/Honolulu", label: "Hawaii (HT)", region: "North America" },
  { value: "America/Phoenix", label: "Arizona (MST, no DST)", region: "North America" },
  { value: "America/Toronto", label: "Toronto (ET)", region: "North America" },
  { value: "America/Vancouver", label: "Vancouver (PT)", region: "North America" },
  { value: "America/Sao_Paulo", label: "São Paulo (BRT)", region: "South America" },

  // Europe
  { value: "Europe/London", label: "London, Dublin, Edinburgh (GMT/BST)", region: "Europe" },
  { value: "Europe/Paris", label: "Paris, Madrid, Amsterdam (CET/CEST)", region: "Europe" },
  { value: "Europe/Berlin", label: "Berlin, Frankfurt, Rome (CET/CEST)", region: "Europe" },
  { value: "Europe/Zurich", label: "Zurich (CET/CEST)", region: "Europe" },
  { value: "Europe/Athens", label: "Athens, Helsinki, Bucharest (EET/EEST)", region: "Europe" },

  // Asia / Middle East / Pacific
  { value: "Asia/Jerusalem", label: "Jerusalem (IST/IDT)", region: "Middle East" },
  { value: "Asia/Dubai", label: "Dubai, Gulf Standard Time (GST)", region: "Middle East" },
  { value: "Asia/Kolkata", label: "India Standard Time (IST)", region: "Asia" },
  { value: "Asia/Bangkok", label: "Bangkok, Hanoi, Jakarta (ICT)", region: "Asia" },
  { value: "Asia/Singapore", label: "Singapore (SGT)", region: "Asia" },
  { value: "Asia/Hong_Kong", label: "Hong Kong (HKT)", region: "Asia" },
  { value: "Asia/Tokyo", label: "Tokyo, Osaka, Sapporo (JST)", region: "Asia" },
  { value: "Asia/Seoul", label: "Seoul (KST)", region: "Asia" },
  { value: "Australia/Sydney", label: "Sydney, Melbourne (AEST/AEDT)", region: "Australia & Pacific" },
  { value: "Australia/Perth", label: "Perth (AWST)", region: "Australia & Pacific" },
  { value: "Pacific/Auckland", label: "Auckland, Wellington (NZST/NZDT)", region: "Australia & Pacific" },

  // Standard UTC
  { value: "UTC", label: "Coordinated Universal Time (UTC)", region: "Global" },
];

/**
 * Returns user's detected local timezone or default fallback.
 */
export function getDefaultTimezone(): string {
  if (typeof Intl !== "undefined" && typeof Intl.DateTimeFormat === "function") {
    try {
      const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (resolved) return resolved;
    } catch {
      // fallback
    }
  }
  return "America/New_York";
}

/**
 * Converts "HH:MM" (24-hour) string to "h:MM AM/PM" (12-hour) string.
 * Example: "18:30" -> "6:30 PM", "00:00" -> "12:00 AM", "12:05" -> "12:05 PM".
 */
export function format24to12(time24: string): string {
  if (!time24 || !time24.includes(":")) return time24;
  const [hStr, mStr] = time24.split(":");
  const h = Number(hStr);
  const m = Number(mStr);
  if (isNaN(h) || isNaN(m)) return time24;

  const period = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  const paddedMinute = String(m).padStart(2, "0");
  return `${hour12}:${paddedMinute} ${period}`;
}

/**
 * Formats a 24-hour "HH:MM" time string according to user's 12h/24h preference.
 * Defaults to 12-hour AM/PM unless hour12 is explicitly false.
 */
export function formatTimeDisplay(time24: string, hour12 = true): string {
  if (!hour12) return time24;
  return format24to12(time24);
}

/**
 * Formats a Date/timestamp into localized time string.
 * Defaults to 12-hour AM/PM (hour12 = true).
 */
export function formatLocalizedTime(
  dateInput: Date | string | number,
  options: {
    timezone?: string;
    hour12?: boolean;
    includeSeconds?: boolean;
  } = {}
): string {
  const { timezone, hour12 = true, includeSeconds = false } = options;
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return "";

  try {
    const tz = timezone || getDefaultTimezone();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      hour: "numeric",
      minute: "2-digit",
      second: includeSeconds ? "2-digit" : undefined,
      hour12,
    });
    return formatter.format(d);
  } catch {
    return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12 });
  }
}

/**
 * Formats a Date/timestamp into localized date + time string (e.g. "Oct 14, 2026, 3:30 PM").
 */
export function formatLocalizedDateTime(
  dateInput: Date | string | number,
  options: {
    timezone?: string;
    hour12?: boolean;
    includeYear?: boolean;
  } = {}
): string {
  const { timezone, hour12 = true, includeYear = true } = options;
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput);
  if (isNaN(d.getTime())) return "";

  try {
    const tz = timezone || getDefaultTimezone();
    const formatter = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      month: "short",
      day: "numeric",
      year: includeYear ? "numeric" : undefined,
      hour: "numeric",
      minute: "2-digit",
      hour12,
    });
    return formatter.format(d);
  } catch {
    return `${d.toLocaleDateString([], { month: "short", day: "numeric" })}, ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", hour12 })}`;
  }
}

/**
 * Formats a time range (e.g. "18:30" to "08:30") respecting 12h/24h setting.
 */
export function formatTimeRangeDisplay(
  start24: string,
  end24: string,
  hour12 = true
): string {
  const start = formatTimeDisplay(start24, hour12);
  const end = formatTimeDisplay(end24, hour12);
  return `${start} – ${end}`;
}
